// hooks/useDragDrop.js
/**
 * useDragDrop — role-assignment and unassignment logic.
 * Drag-and-drop removed; assignment is via the Assign button in each role's accordion section.
 *
 * Manages:
 *  - assignCandidateToRole
 *  - unassignCandidateFromRole
 *  - removeAssignment / performRemoveAssignment
 */

import { useCallback } from 'react';
import { Logger } from '../../../utils/logger';

const logger = new Logger('useDragDrop');

/**
 * @param {object} params
 * @param {object|null}  params.staffingData
 * @param {function}     params.setStaffingData
 * @param {object|null}  params.optimizationResult
 * @param {function}     params.setOptimizationResult
 * @param {function}     params.setLoadedConfigurationId
 * @param {function}     params.setLoadedConfigurationName
 * @param {function}     params.setInjectedChemistryData
 * @param {function}     params.scheduleAutoRefresh
 * @param {function}     params.onUnassigned — called with roleId after a role is unassigned
 */
export function useDragDrop({
  staffingData,
  setStaffingData,
  optimizationResult,
  setOptimizationResult,
  setLoadedConfigurationId,
  setLoadedConfigurationName,
  setInjectedChemistryData,
  scheduleAutoRefresh,
  onUnassigned = null,
}) {
  // ── Assign candidate to role ──────────────────────────────────────────────
  const assignCandidateToRole = useCallback((roleId, candidate) => {
    setInjectedChemistryData(null); // team changed — allow fresh chemistry fetch

    logger.debug('🔍 DEBUG: Candidate object being assigned:', {
      employee_id: candidate.employee_id,
      name: `${candidate.first_name} ${candidate.last_name}`,
      availability_reason: candidate.availability_reason,
      availability_tier: candidate.availability_tier,
      availability_object: candidate.availability,
      all_keys: Object.keys(candidate)
    });

    setStaffingData(prev => {
      const updatedRoles = prev.roles.map(role => {
        if (role.id === roleId) {
          return {
            ...role,
            status: 'assigned',
            assigned_employee_id: candidate.employee_id,
            assigned_employee_name: `${candidate.first_name} ${candidate.last_name}`,
            fit_score: candidate.fit_score,
            assigned_tech_score: candidate.overall_technical_score,
            assigned_hourly_rate: candidate.hourly_rate,
            assigned_availability_reason: candidate.availability_reason,
            assigned_availability_tier: candidate.availability?.tier || candidate.availability_tier,
            assigned_next_available_date: candidate.availability?.next_available_date || candidate.next_available_date
          };
        }
        return role;
      });

      const filledRoles = updatedRoles.filter(r => r.status === 'assigned').length;
      const totalRoles = updatedRoles.length;
      const completionPercentage = totalRoles > 0 ? Math.round((filledRoles / totalRoles) * 100) : 0;

      return {
        ...prev,
        roles: updatedRoles,
        progress: { totalRoles, filledRoles, completionPercentage }
      };
    });

    if (optimizationResult) setOptimizationResult(null);

    // Team changed — clear loaded config so next analysis save creates a fresh record
    setLoadedConfigurationId(null);
    setLoadedConfigurationName(null);
  }, [
    setInjectedChemistryData, setStaffingData,
    optimizationResult, setOptimizationResult,
    setLoadedConfigurationId, setLoadedConfigurationName,
  ]);

  // ── Unassign candidate from role ──────────────────────────────────────────
  const unassignCandidateFromRole = useCallback((roleId) => {
    setInjectedChemistryData(null);

    setStaffingData(prev => {
      const updatedRoles = prev.roles.map(role => {
        if (role.id === roleId) {
          return {
            ...role,
            status: 'open',
            assigned_employee_id: null,
            assigned_employee_name: null,
            fit_score: null,
            assigned_tech_score: null,
            assigned_hourly_rate: null,
            assigned_availability_reason: null,
            assigned_availability_tier: null,
            assigned_next_available_date: null
          };
        }
        return role;
      });
      const filledRoles = updatedRoles.filter(r => r.status === 'assigned').length;
      const totalRoles = updatedRoles.length;
      return {
        ...prev,
        roles: updatedRoles,
        progress: { totalRoles, filledRoles, completionPercentage: totalRoles > 0 ? Math.round((filledRoles / totalRoles) * 100) : 0 }
      };
    });

    if (optimizationResult) setOptimizationResult(null);
    setLoadedConfigurationId(null);
    setLoadedConfigurationName(null);
    onUnassigned?.(roleId);
  }, [
    setInjectedChemistryData, setStaffingData,
    optimizationResult, setOptimizationResult,
    setLoadedConfigurationId, setLoadedConfigurationName,
    onUnassigned,
  ]);

  // ── Remove assignment (button click) ──────────────────────────────────────
  const performRemoveAssignment = useCallback((roleId) => {
    setInjectedChemistryData(null);

    setStaffingData(prev => {
      const updatedRoles = prev.roles.map(role => {
        if (role.id === roleId) {
          return {
            ...role,
            status: 'open',
            assigned_employee_id: null,
            assigned_employee_name: null,
            fit_score: null
          };
        }
        return role;
      });

      const filledRoles = updatedRoles.filter(r => r.status === 'assigned').length;
      const totalRoles = updatedRoles.length;
      const completionPercentage = totalRoles > 0 ? Math.round((filledRoles / totalRoles) * 100) : 0;

      return {
        ...prev,
        roles: updatedRoles,
        progress: { totalRoles, filledRoles, completionPercentage }
      };
    });

    if (optimizationResult) {
      logger.debug('🔄 Clearing team analysis (team composition changed after removal)');
      setOptimizationResult(null);
    }

    scheduleAutoRefresh?.();
    onUnassigned?.(roleId);
  }, [
    setInjectedChemistryData, setStaffingData,
    optimizationResult, setOptimizationResult,
    scheduleAutoRefresh, onUnassigned,
  ]);

  const removeAssignment = useCallback((roleId) => {
    performRemoveAssignment(roleId);
  }, [performRemoveAssignment]);

  return {
    assignCandidateToRole,
    unassignCandidateFromRole,
    removeAssignment,
  };
}
